import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Read/write list state through the URL.
 *
 * Filters, sort, page, and search live in the query string so a view is
 * shareable and the back button behaves — the same contract the Django
 * changelists had.
 */
export function useQueryParamState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = React.useCallback(
    (key: string, fallback = '') => searchParams.get(key) ?? fallback,
    [searchParams],
  );

  /**
   * Merge updates into the query string. `null`/`''` removes a key. Any change
   * other than the page itself resets to page 1, since the old page number is
   * meaningless against a different result set.
   */
  const update = React.useCallback(
    (changes: Record<string, string | number | null | undefined>, options?: { replace?: boolean }) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          }
          const touchesOnlyPage = Object.keys(changes).every((key) => key === 'page');
          if (!touchesOnlyPage) next.delete('page');
          return next;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const clear = React.useCallback(
    (keep: string[] = []) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams();
        for (const key of keep) {
          const value = previous.get(key);
          if (value) next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  /** Replace the entire query string (saved views, quick filters, chip remove). */
  const replaceQuery = React.useCallback(
    (query: string, options?: { replace?: boolean }) => {
      setSearchParams(new URLSearchParams(query.replace(/^\?/, '')), {
        replace: options?.replace ?? false,
      });
    },
    [setSearchParams],
  );

  /** Plain object of the current params, for use as a TanStack Query key. */
  const asObject = React.useMemo(() => {
    const record: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }, [searchParams]);

  return {
    searchParams,
    get,
    update,
    clear,
    replaceQuery,
    asObject,
    queryString: searchParams.toString(),
  };
}

/**
 * Debounced text input bound to a query param.
 *
 * The input stays responsive locally while the URL — and therefore the request —
 * only updates once typing settles.
 */
export function useDebouncedParam(
  key: string,
  delayMs = 300,
): [string, (value: string) => void] {
  const { get, update } = useQueryParamState();
  const committed = get(key);
  const [draft, setDraft] = React.useState(committed);
  const draftRef = React.useRef(draft);
  draftRef.current = draft;

  // Keep the box in sync when the URL changes from elsewhere (chip removal,
  // saved view, back button) without clobbering in-flight typing.
  React.useEffect(() => {
    if (committed !== draftRef.current) setDraft(committed);
  }, [committed]);

  React.useEffect(() => {
    if (draft === committed) return undefined;
    const timer = setTimeout(() => {
      // replace: true so a search does not create a history entry per keystroke.
      update({ [key]: draft }, { replace: true });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [draft, committed, delayMs, key, update]);

  return [draft, setDraft];
}
